import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";

import { apiGet } from "@/api/client";

import { resolveThemeAsset } from "./assets";
import { neutralTheme } from "./presets";
import {
  publicSettingsSchema,
  themeV1Schema,
  type PublicSettings,
  type ThemeV1,
} from "./schema";

const CACHE_VERSION = 2;
const DEFAULT_TITLE = "Horseracing World";
let bootstrappedSettings: PublicSettings | null = null;
let bootstrappedAt = 0;
const defaultFaviconHref =
  typeof document === "undefined"
    ? null
    : (document.querySelector<HTMLLinkElement>('link[rel~="icon"]')?.href ??
      null);

type VisualThemeCache = {
  version: typeof CACHE_VERSION;
  host: string;
  tenantSlug: string;
  theme: ThemeV1;
};

type ThemeContextValue = {
  theme: ThemeV1;
  tenantSlug: string;
  settings: PublicSettings | null;
  isLoading: boolean;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function cacheKey() {
  return `hrw.theme.visual.v2:${window.location.host}`;
}

function legacyCacheKey() {
  return `hrw.theme.v1:${window.location.host}`;
}

function readVisualThemeCache(): VisualThemeCache | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(cacheKey());
    if (!raw) return null;
    const value = JSON.parse(raw) as Record<string, unknown>;
    if (
      value.version !== CACHE_VERSION ||
      value.host !== window.location.host ||
      typeof value.tenantSlug !== "string"
    ) {
      return null;
    }
    const parsed = themeV1Schema.safeParse(value.theme);
    if (!parsed.success) return null;
    return {
      version: CACHE_VERSION,
      host: window.location.host,
      tenantSlug: value.tenantSlug,
      theme: parsed.data,
    };
  } catch {
    return null;
  }
}

function writeVisualThemeCache(settings: PublicSettings) {
  const value: VisualThemeCache = {
    version: CACHE_VERSION,
    host: window.location.host,
    tenantSlug: settings.tenant.slug,
    theme: settings.tenant.theme,
  };
  try {
    window.localStorage.setItem(cacheKey(), JSON.stringify(value));
    window.localStorage.removeItem(legacyCacheKey());
  } catch {
    // Theme caching is an optional visual optimization.
  }
}

export function applyThemeToDocument(theme: ThemeV1) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.theme = theme.preset;
  root.dataset.shell = theme.shellVariant;
  root.dataset.font = theme.fontPreset;

  const variables = {
    "--bg": theme.colors.page,
    "--surface": theme.colors.surface,
    "--surface-3": theme.colors.surfaceRaised,
    "--surface-raised": theme.colors.surfaceRaised,
    "--line": theme.colors.border,
    "--text": theme.colors.text,
    "--text-muted": theme.colors.textMuted,
    "--primary": theme.colors.primary,
    "--primary-strong": theme.colors.primaryStrong,
    "--primary-hover": theme.colors.primaryHover,
    "--on-primary": theme.colors.onPrimary,
  };
  for (const [name, value] of Object.entries(variables)) {
    root.style.setProperty(name, value);
  }

  document.title =
    theme.preset === "neutral"
      ? DEFAULT_TITLE
      : `${theme.brand.displayName} · MonkeyTilt`;
  document
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute("content", theme.colors.page);

  const favicon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  const themedFavicon = resolveThemeAsset(theme.assets.faviconKey);
  if (favicon && (themedFavicon || defaultFaviconHref)) {
    favicon.href = themedFavicon ?? defaultFaviconHref ?? favicon.href;
  }
}

export function bootstrapCachedTheme() {
  const cached = readVisualThemeCache();
  if (cached) applyThemeToDocument(cached.theme);
}

export async function bootstrapThemeBeforeRender(timeoutMs = 1_500) {
  const cached = readVisualThemeCache();
  if (cached) {
    applyThemeToDocument(cached.theme);
    return;
  }

  let timer = 0;
  try {
    const timeout = new Promise<never>((_, reject) => {
      timer = window.setTimeout(
        () => reject(new Error("Theme bootstrap timed out")),
        timeoutMs,
      );
    });
    const settings = await Promise.race([
      apiGet("/public/settings", publicSettingsSchema),
      timeout,
    ]);
    bootstrappedSettings = settings;
    bootstrappedAt = Date.now();
    applyThemeToDocument(settings.tenant.theme);
    writeVisualThemeCache(settings);
  } catch {
    applyThemeToDocument(neutralTheme);
  } finally {
    window.clearTimeout(timer);
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const cached = useMemo(() => readVisualThemeCache(), []);
  const settingsQuery = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => apiGet("/public/settings", publicSettingsSchema),
    initialData: bootstrappedSettings ?? undefined,
    initialDataUpdatedAt: bootstrappedAt || undefined,
    staleTime: 60_000,
  });
  const theme =
    settingsQuery.data?.tenant.theme ?? cached?.theme ?? neutralTheme;

  useEffect(() => {
    applyThemeToDocument(theme);
    if (settingsQuery.data) writeVisualThemeCache(settingsQuery.data);
  }, [settingsQuery.data, theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      tenantSlug:
        settingsQuery.data?.tenant.slug ??
        cached?.tenantSlug ??
        window.location.host,
      settings: settingsQuery.data ?? null,
      isLoading: settingsQuery.isLoading && cached === null,
    }),
    [cached, settingsQuery.data, settingsQuery.isLoading, theme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme must be used within ThemeProvider");
  return value;
}
